import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useIsMobile } from '../hooks/useMediaQuery';
import '../styles/Modal.scss';

// Reusable modal: a centered dialog on desktop, a bottom sheet on mobile. The overlay
// is always rendered and shown/hidden purely via the `is-open` class (CSS opacity +
// visibility transitions), which avoids mount/unmount timing issues inside the app's
// route-level <AnimatePresence mode="wait">. Portaled to <body> so it's never clipped.
function Modal({ open, onClose, labelledBy, wide = false, children }) {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return createPortal(
    <div
      className={`app-modal-overlay${isMobile ? ' is-sheet' : ''}${open ? ' is-open' : ''}`}
      onClick={onClose}
      aria-hidden={!open}
    >
      <div
        className={`app-modal${wide ? ' app-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && <div className="app-modal-handle" aria-hidden="true" />}

        <button className="app-modal-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="app-modal-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default Modal;
